import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import EmailVerification from './EmailVerification';
import PhoneVerification from './PhoneVerification';
import UpdateEmail from './UpdateEmail';
import InviteFormComponent from './InviteFormComponent';
import { getToken } from '../utils/auth';
import LoadingScreen from '../components/LoadingScreen';

const API_BASE = import.meta.env.VITE_API_BASE;

// Registration states as defined in the backend
const RegistrationStates = {
  LOGIN: 'Login',
  INVITE_FORM: 'InviteForm',
  MAIL_VERIFICATION: 'MailVerification',
  PHONE_VERIFICATION: 'PhoneVerification'
};

export default function Registration() {
  const { regToken } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registrationState, setRegistrationState] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [checkingStatus, setCheckingStatus] = useState(false);
  const effectRun = useRef(false);
  const [debugInfo, setDebugInfo] = useState(null);

  // Function to check the current registration state
  const checkState = async () => {
    if (checkingStatus) return;
    setCheckingStatus(true);
    
    console.log("Checking registration state for token:", regToken);
    
    try {
      // Get the current registration state from the backend
      const stateResponse = await fetch(`${API_BASE}/signup/${regToken}/check`);
      console.log("State response status:", stateResponse.status);
      
      if (stateResponse.status === 404) {
        console.log("Registration not found (404)");
        sessionStorage.removeItem('registrationToken');
        navigate('/signup');
        return;
      }

      if (stateResponse.status === 401) {
        console.log("Unauthorized (401), redirecting to login");
        localStorage.clear();
        sessionStorage.removeItem('registrationToken');
        navigate('/login', { replace: true });
        return;
      }

      if (!stateResponse.ok) {
        throw new Error(`Server returned ${stateResponse.status}: ${stateResponse.statusText}`);
      }

      // Parse the registration state
      const stateData = await stateResponse.json();
      console.log("Registration state:", stateData);
      
      if (!stateData || !stateData.state) {
        throw new Error('Invalid response from server: missing state information');
      }
      
      // Store the registration state
      setRegistrationState(stateData.state);
      sessionStorage.setItem('registrationToken', regToken);
      
      // Handle redirection based on state
      if (stateData.state === RegistrationStates.LOGIN) {
        console.log("Registration complete, redirecting to login");
        sessionStorage.removeItem('registrationToken');
        navigate('/login', { replace: true });
        return;
      }
      
      // For invite form, redirect to the specific route
      if (stateData.state === RegistrationStates.INVITE_FORM && !location.pathname.endsWith('/invite')) {
        console.log("Redirecting to invite form");
        const searchParams = new URLSearchParams(location.search);
        const key = searchParams.get('key');
        navigate(`/signup/${regToken}/invite?from=email&invite=true${key ? `&key=${key}` : ''}`, { replace: true });
        return;
      }
      
      // For phone verification, direct to the dedicated route
      if (stateData.state === RegistrationStates.PHONE_VERIFICATION && !location.pathname.endsWith('/phoneverification')) {
        console.log("Redirecting to phone verification component");
        navigate(`/signup/${regToken}/phoneverification`, { replace: true });
        return;
      }
      
      // Get email for mail verification if needed
      if (stateData.state === RegistrationStates.MAIL_VERIFICATION) {
        try {
          const mailResponse = await fetch(`${API_BASE}/signup/${regToken}/mailv`);
          if (mailResponse.ok) {
            const mailData = await mailResponse.json();
            if (mailData.email) {
              setUserEmail(mailData.email);
            }
          }
        } catch (err) {
          console.error("Failed to get email:", err);
        }
        // Explicitly set loading to false after setting state to ensure EmailVerification component is rendered
        setLoading(false);
      }
      
    } catch (err) {
      console.error('Failed to check registration state:', err);
      setError(`Failed to verify registration: ${err.message || 'Unknown error'}. Please try again.`);
    } finally {
      setCheckingStatus(false);
      setLoading(false);
    }
  };

  // Function to manually debug the API
  const debugCheckAPI = async () => {
    setDebugInfo('Loading...');
    try {
      // Make a raw request to check the API response
      const response = await fetch(`${API_BASE}/signup/${regToken}/check`);
      const responseText = await response.text();
      
      let formattedResponse;
      try {
        // Try to parse as JSON to format nicely
        const jsonData = JSON.parse(responseText);
        formattedResponse = JSON.stringify(jsonData, null, 2);
      } catch (e) {
        // If not valid JSON, show as text
        formattedResponse = responseText;
      }
      
      setDebugInfo(`
Status: ${response.status} ${response.statusText}
Headers: ${JSON.stringify(Object.fromEntries([...response.headers]), null, 2)}
Body: ${formattedResponse}
      `);
    } catch (err) {
      setDebugInfo(`Error making request: ${err.message}`);
    }
  };

  useEffect(() => {
    if (effectRun.current) return;
    effectRun.current = true;
    
    if (!regToken) {
      const savedToken = sessionStorage.getItem('registrationToken');
      if (savedToken) {
        navigate(`/signup/${savedToken}`);
      } else {
        navigate('/signup');
      }
      return;
    }

    // Check specific routes first
    if (location.pathname.endsWith('/invite')) {
      // Let the InviteFormComponent handle its own logic
      setLoading(false);
      return;
    }
    
    if (location.pathname.endsWith('/updateemail')) {
      // Let the UpdateEmail component handle its own logic
      setLoading(false);
      return;
    }
    
    if (location.pathname.endsWith('/phoneverification')) {
      // Let PhoneVerification handle its own logic
      setLoading(false);
      return;
    }

    // Check the registration state
    checkState();
    
    // Set a timeout to prevent infinite loading - use a shorter timeout
    const timeoutId = setTimeout(() => {
      if (loading) {
        console.log("Loading timeout reached, forcing loading to complete");
        setLoading(false);
        // Don't set any error message
      }
    }, 3000); // Reduced from 5000 to 3000ms
    
    return () => clearTimeout(timeoutId);
  }, [regToken, navigate, location.pathname]);

  // Add an additional safety timeout on component mount to ensure we never get stuck
  useEffect(() => {
    const safetyTimeout = setTimeout(() => {
      if (loading) {
        console.log("Safety timeout triggered to prevent infinite loading");
        setLoading(false);
      }
    }, 5000);
    
    return () => clearTimeout(safetyTimeout);
  }, []);

  // Debug log
  console.log("Registration component state:", {
    loading,
    registrationState,
    error,
    pathname: location.pathname
  });

  const handleRegistrationFailed = () => {
    sessionStorage.removeItem('registrationToken');
    navigate('/signup');
  };

  // Add visual indication of current state
  const getRegistrationStateDisplay = () => {
    // Remove the registration state display from UI
    return null;
  };

  // Render the appropriate component based on registration state
  const renderContent = () => {
    if (location.pathname.endsWith('/invite')) {
      return <InviteFormComponent />;
    } 
    
    if (location.pathname.endsWith('/updateemail')) {
      return <UpdateEmail regToken={regToken} />;
    }
    
    switch (registrationState) {
      case RegistrationStates.MAIL_VERIFICATION:
        console.log("Rendering EmailVerification component");
        return (
          <EmailVerification 
            regToken={regToken} 
            userEmail={userEmail}
            onRegistrationFailed={handleRegistrationFailed}
          />
        );
      case RegistrationStates.PHONE_VERIFICATION:
        console.log("Rendering PhoneVerification component");
        return (
          <PhoneVerification 
            regToken={regToken} 
            onRegistrationFailed={handleRegistrationFailed} 
          />
        );
      case RegistrationStates.LOGIN:
        // Should be handled by the redirect, but just in case
        return (
          <div className="text-center py-8">
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-4">Registration Complete!</h2>
            <p className="text-[var(--text-secondary)] mb-6">Your account has been successfully set up.</p>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-md hover:bg-[var(--hover-dark)] transition-colors"
            >
              Go to Login
            </button>
          </div>
        );
      default:
        // If we don't have a state yet but loading is done, show a simplified error
        if (!loading) {
          return (
            <div className="text-center">
              <button
                onClick={() => {
                  setLoading(true);
                  setError(null);
                  setTimeout(() => checkState(), 100);
                }}
                className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-md hover:bg-[var(--hover-dark)] transition-colors"
              >
                Retry
              </button>
            </div>
          );
        }
        return null;
    }
  };

  useEffect(() => {
    // Set a backup plan - if we detect PhoneVerification state but stay on loading screen,
    // force render the PhoneVerification component after a short delay
    if (registrationState === RegistrationStates.PHONE_VERIFICATION && loading) {
      const timeoutId = setTimeout(() => {
        console.log("Forcing rendering of PhoneVerification component due to detected state");
        setLoading(false);
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [registrationState, loading]);

  // Add an effect to explicitly handle mail verification state
  useEffect(() => {
    // If we have detected MailVerification state but still show loading screen,
    // force render the EmailVerification component after a short delay
    if (registrationState === RegistrationStates.MAIL_VERIFICATION && loading) {
      console.log("Force rendering EmailVerification component due to detected state");
      const timeoutId = setTimeout(() => {
        setLoading(false);
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [registrationState, loading]);

  return (
    <div className="max-w-sm w-full sm:max-w-md p-4 my-8 bg-[var(--card-bg)] rounded-lg border border-[var(--border-color)] space-y-2">
      {/* Remove registration state display */}
      {loading ? (
        <LoadingScreen />
      ) : error ? (
        <div className="text-center min-h-[350px] flex flex-col items-center justify-center">
          <p className="text-[var(--text-primary)] mb-4">{error}</p>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => navigate('/signup')}
              className="text-[var(--accent-primary)] hover:text-[var(--hover-dark)] text-sm font-medium"
            >
              Return to signup
            </button>
            <button
              onClick={() => {
                setLoading(true);
                setError(null);
                setTimeout(() => checkState(), 100);
              }}
              className="text-[var(--accent-primary)] hover:text-[var(--hover-dark)] text-sm font-medium"
            >
              Retry Check
            </button>
          </div>
        </div>
      ) : (
        renderContent()
      )}
    </div>
  );
} 